import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, FileImage, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '../lib/imageUtils';

export default function BrandPayslipsPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'management'].includes(user?.system_role);
  const isBrandStaff = !!user?.brand_classification;
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadMonth, setUploadMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [viewer, setViewer] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setList([]);
    try {
      const res = await api.get('/brand-payslips', { params: { month } });
      setList(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      await api.post('/brand-payslips', { month: uploadMonth, payslip_base64: compressed });
      toast.success(`Payslip for ${uploadMonth} uploaded`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ''; }
  };

  const handleView = async (id) => {
    try {
      const res = await api.get(`/brand-payslips/${id}/image`);
      setViewer(res.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.employee_name}'s ${item.month} payslip?`)) return;
    try { await api.delete(`/brand-payslips/${item.id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="brand-payslips-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Brand Staff Payslips</h1>
          <p className="text-neutral-500 text-sm mt-0.5">
            {isBrandStaff ? 'Upload your external (brand-issued) payslip screenshot for management to view.' : 'View payslips uploaded by brand staff.'}
          </p>
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="brand-payslip-month-filter" />
      </div>

      {isBrandStaff && (
        <Card className="border border-amber-200 bg-amber-50/30 shadow-sm rounded-md" data-testid="upload-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Upload className="h-4 w-4 text-amber-600" /> Upload Your Payslip Screenshot
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 flex flex-col sm:flex-row gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs uppercase text-neutral-500">Month</Label>
              <Input type="month" value={uploadMonth} onChange={e => setUploadMonth(e.target.value)}
                className="rounded-sm" data-testid="upload-month-input" />
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-400 bg-white rounded-sm cursor-pointer text-sm font-semibold text-amber-700 hover:bg-amber-50" data-testid="upload-btn">
              <FileImage className="h-4 w-4" /> Choose image
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="upload-input" />
            </label>
            <p className="text-[10px] text-neutral-500">Re-uploading for the same month replaces the existing image.</p>
          </CardContent>
        </Card>
      )}

      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">Payslips for {month} ({list.length})</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="text-xs uppercase">Employee</TableHead>
                  <TableHead className="text-xs uppercase">Brand</TableHead>
                  <TableHead className="text-xs uppercase">Month</TableHead>
                  <TableHead className="text-xs uppercase">Uploaded</TableHead>
                  <TableHead className="text-xs uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-neutral-400">Loading…</TableCell></TableRow>
                ) : list.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-neutral-500">No payslips uploaded for {month}</TableCell></TableRow>
                ) : list.map(p => (
                  <TableRow key={p.id} data-testid={`payslip-row-${p.id}`}>
                    <TableCell className="text-sm font-medium">{p.employee_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{p.brand_classification}</Badge></TableCell>
                    <TableCell className="text-sm">{p.month}</TableCell>
                    <TableCell className="text-xs text-neutral-500">{new Date(p.uploaded_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(p.id)} data-testid={`view-payslip-${p.id}`}><Eye className="h-4 w-4 text-blue-600" /></Button>
                        {(isAdmin || p.employee_id === user?.id) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(p)} data-testid={`delete-payslip-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-2xl" data-testid="payslip-viewer-dialog">
          <DialogHeader><DialogTitle className="font-heading font-bold">{viewer?.employee_name} · {viewer?.month}</DialogTitle></DialogHeader>
          {viewer?.payslip_base64 && (
            <img src={viewer.payslip_base64} alt="payslip" className="w-full max-h-[70vh] object-contain rounded-sm border border-neutral-200" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
